import { useCallback, useMemo, useEffect } from 'react';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { SubscriptionContent } from '@dynamika-shared/types/subscription';
import { StateFilterItem, AllFilters } from '@components/NestedFilter/types';
import { useClinicalTrialSubscriptionSlice } from '@restore/clinicalTrialSubscription/clinicalTrialSubscription.slice';
import { PossibleSubscriptionFilters } from '@restore/clinicalTrialSubscription/subscriptionFilters.types';

const FILTER_GROUP_KEY = 'subscription-filters-group';

const isPossibleSubscriptionFilter = (
  key: string
): key is PossibleSubscriptionFilters => {
  return key === 'scope' || key === 'type';
};

// NOTE: UI-only display labels — the filter value sent to the backend stays
// the raw subscriptionType (EMAIL / USER).
const TYPE_LABELS: Record<string, string> = {
  EMAIL: 'External User',
  USER: 'System User',
};

const formatTypeLabel = (value: string) => {
  if (!value) return value;
  return (
    TYPE_LABELS[value] ??
    value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
  );
};

const getScopesFromSubscription = (scope?: string): string[] => {
  if (!scope || scope === '') return ['All sites'];

  return scope
    .split(';')
    .map(item => item.trim())
    .filter(item => Boolean(item));
};

export const useSubscriptionFilters = (
  subscriptions: SubscriptionContent[]
) => {
  const {
    filterState,
    selectedFilters,
    allFilters: storedAllFilters,
    toggleActiveFilters,
    toggleSelectedFilters,
    setAllFilters,
    resetFilters,
  } = useClinicalTrialSubscriptionSlice((state, actions) => ({
    filterState: state.filters.state,
    selectedFilters: state.filters.selectedFilters,
    allFilters: state.filters.allFilters,
    toggleActiveFilters: actions.toggleActiveFilters,
    toggleSelectedFilters: actions.toggleSelectedFilters,
    setAllFilters: actions.setAllFilters,
    resetFilters: actions.resetFilters,
  }));

  const computedAllFilters = useMemo(() => {
    const scopeSet = new Set<string>();
    const typeSet = new Set<string>();

    subscriptions.forEach(subscription => {
      getScopesFromSubscription(subscription.scope).forEach(scope => {
        scopeSet.add(scope);
      });

      if (subscription.subscriptionType) {
        typeSet.add(subscription.subscriptionType);
      }
    });

    return {
      scope: [
        ...Array.from(scopeSet)
          .sort((a, b) => a.localeCompare(b))
          .map(scope => ({
            value: scope,
            label: scope,
          })),
      ],
      type: Array.from(typeSet)
        .sort((a, b) => a.localeCompare(b))
        .map(type => ({
          value: type,
          label: formatTypeLabel(type),
        })),
    } as AllFilters<PossibleSubscriptionFilters, string>;
  }, [subscriptions]);

  useEffect(() => {
    setAllFilters(computedAllFilters);
  }, [computedAllFilters, setAllFilters]);

  const stateEntries = useMemo(
    () =>
      Object.entries(filterState) as [
        PossibleSubscriptionFilters,
        StateFilterItem,
      ][],
    [filterState]
  );

  const filters = useMemo(() => {
    return [
      {
        key: FILTER_GROUP_KEY,
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: stateEntries.map(([key, filter]) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={filter.active} />
            </div>
          ),
        })),
      },
    ];
  }, [stateEntries]);

  const toggleActiveFilter = useCallback(
    (name: PossibleSubscriptionFilters) => {
      toggleActiveFilters(name);
    },
    [toggleActiveFilters]
  );

  const toggleSelectedFilter = useCallback(
    ({ name, value }: { name: PossibleSubscriptionFilters; value: string }) => {
      toggleSelectedFilters({ name, value });
    },
    [toggleSelectedFilters]
  );

  const filterOnClick = useCallback(
    (item: MenuInfo) => {
      const key = String(item.key);

      if (isPossibleSubscriptionFilter(key)) {
        toggleActiveFilter(key);
      }
    },
    [toggleActiveFilter]
  );

  const args = () => ({
    scope: selectedFilters.scope,
    type: selectedFilters.type,
  });

  const reset = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  return {
    subFilters: stateEntries,
    filters,
    allFilters: storedAllFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    args,
    reset,
  };
};
